import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, MessageCircle, Check, Bell, UserPlus, UserCheck } from 'lucide-react';
import { timeAgo } from '../utils';

export default function NotificationDropdown({
  notifications,
  onMarkRead,
  onMarkAllRead,
  onClose,
}) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const navigate = useNavigate();

  // Fermer au clic extérieur
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const unread = notifications.filter((n) => !n.read);

  const handleNotifClick = (notif) => {
    if (!notif.read) onMarkRead(notif.id);
    if (notif.type === 'match' && notif.conversationId) {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'match') {
      navigate('/matches');
    } else if (notif.type === 'message') {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'like') {
      navigate('/matches?tab=received');
    } else if (notif.type === 'friend_request') {
      navigate('/');
    } else if (notif.type === 'friend_accepted' && notif.conversationId) {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'friend_accepted') {
      navigate('/members');
    } else {
      navigate('/');
    }
    onClose();
  };

  return (
    <div
      ref={ref}
      className="fixed inset-x-4 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 w-auto sm:w-80 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-800 z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {t('notifDropdown.title')}
        </h3>
        <div className="flex items-center gap-2">
          {unread.length > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1"
            >
              <Check className="w-3 h-3" />
              {t('notifDropdown.markRead')}
            </button>
          )}
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 font-medium"
          >
            {t('notifDropdown.viewAll')}
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="max-h-[60vh] sm:max-h-80 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <Bell className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-400">{t('notifDropdown.empty')}</p>
          </div>
        ) : (
          notifications.slice(0, 10).map((notif) => (
            <button
              key={notif.id}
              onClick={() => handleNotifClick(notif)}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                !notif.read ? 'bg-rose-50/50 dark:bg-rose-950/20' : ''
              }`}
            >
              {/* Icône */}
              <div
                className={`mt-0.5 flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                  notif.type === 'match'
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-500'
                    : notif.type === 'message'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-500'
                      : notif.type === 'friend_request'
                        ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-500'
                        : notif.type === 'friend_accepted'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500'
                          : 'bg-rose-100 dark:bg-rose-900/30 text-rose-500'
                }`}
              >
                {notif.type === 'match' ? (
                  <Sparkles className="w-4 h-4" />
                ) : notif.type === 'message' ? (
                  <MessageCircle className="w-4 h-4" />
                ) : notif.type === 'friend_request' ? (
                  <UserPlus className="w-4 h-4" />
                ) : notif.type === 'friend_accepted' ? (
                  <UserCheck className="w-4 h-4" />
                ) : (
                  <Heart className="w-4 h-4 fill-current" />
                )}
              </div>

              {/* Contenu */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {notif.type === 'match' ? (
                    <>
                      <span className="font-semibold">
                        {notif.fromName || t('notifDropdown.someone')}
                      </span>{' '}
                      {t('notifDropdown.matchText')}
                    </>
                  ) : notif.type === 'message' ? (
                    <>
                      <span className="font-semibold">
                        {notif.fromName || t('notifDropdown.someone')}
                      </span>
                      {' : '}
                      {notif.messagePreview || t('notifDropdown.newMessage')}
                    </>
                  ) : notif.type === 'friend_request' ? (
                    t('notifications.types.friendRequest', {
                      name: notif.fromName || t('notifDropdown.someone'),
                    })
                  ) : notif.type === 'friend_accepted' ? (
                    t('notifications.types.friendAccepted', {
                      name: notif.fromName || t('notifDropdown.someone'),
                    })
                  ) : (
                    <>{t('notifDropdown.likedProfile')}</>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{timeAgo(notif.createdAt)}</p>
              </div>

              {/* Indicateur non lu */}
              {!notif.read && (
                <div className="mt-2 w-2 h-2 bg-rose-500 rounded-full flex-shrink-0" />
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 10 && (
        <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-2">
          <button
            onClick={() => {
              navigate('/notifications');
              onClose();
            }}
            className="w-full text-center text-sm text-rose-500 hover:text-rose-600 font-medium py-1"
          >
            {t('notifDropdown.viewAllNotifications')}
          </button>
        </div>
      )}
    </div>
  );
}
